import { ApolloCache, Reference, StoreObject } from "@apollo/client";

/**
 * The vocabulary lists (axes, schemas, qualifiers) as the cache holds them, and
 * the typename each one's edges carry. A detail screen finds its row in the
 * list, so a create or delete has to land in the list before the screen that
 * reads it mounts: a refetch alone cannot promise that, because Apollo drops a
 * refetch of a query that navigation tears down, and refetch-by-name misses a
 * list query that is not mounted at all.
 */
const EDGE_TYPENAMES = {
  wsmFitmentAxes: "WsmFitmentAxisCountableEdge",
  wsmFitmentSchemas: "WsmFitmentSchemaCountableEdge",
  wsmFitmentQualifiers: "WsmFitmentQualifierCountableEdge",
} as const;

export type WsmFitmentListField = keyof typeof EDGE_TYPENAMES;

interface StoredConnection {
  edges: Array<{ node: Reference }>;
  pageInfo?: StoreObject | Reference;
}

/**
 * Appends a created row to every stored copy of the list that ends the walk
 * (no next page), once: the lists page in (updatedAt, id) order, so a new row
 * is last. A copy with a next page leaves it to the page that follows.
 */
export const addToWsmFitmentList = (
  cache: ApolloCache<unknown>,
  field: WsmFitmentListField,
  node: StoreObject & { id: string },
) => {
  cache.modify({
    fields: {
      [field]: (existing: StoredConnection | null, { readField, toReference }) => {
        if (!existing?.edges) {
          return existing;
        }

        if (existing.pageInfo && readField<boolean>("hasNextPage", existing.pageInfo)) {
          return existing;
        }

        if (existing.edges.some(edge => readField("id", edge.node) === node.id)) {
          return existing;
        }

        const ref = toReference(node);

        return ref
          ? {
              ...existing,
              edges: [...existing.edges, { __typename: EDGE_TYPENAMES[field], node: ref }],
            }
          : existing;
      },
    },
  });
};

/**
 * Takes a deleted row out of every stored copy of the list, then evicts the
 * row itself, so no screen can read it back from the cache.
 */
export const removeFromWsmFitmentList = (
  cache: ApolloCache<unknown>,
  field: WsmFitmentListField,
  node: { __typename: string; id: string },
) => {
  cache.modify({
    fields: {
      [field]: (existing: StoredConnection | null, { readField }) =>
        existing?.edges
          ? {
              ...existing,
              edges: existing.edges.filter(edge => readField("id", edge.node) !== node.id),
            }
          : existing,
    },
  });

  const cacheId = cache.identify(node);

  if (cacheId) {
    cache.evict({ id: cacheId });
  }

  cache.gc();
};
